import React, { useState, useEffect, useRef } from 'react';
import { Table, Button, Modal, Select, Upload, Switch, message, Card, Spin, Tag, Checkbox, Input, List, Empty } from 'antd';
import { getAuthHeaders, getAuthToken, getAuthHeadersForUpload } from '../utils/authHeaders';
import { useAuth } from '../hooks/useAuth';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { PlusOutlined, UploadOutlined, DeleteOutlined, LoadingOutlined, ClockCircleOutlined, CheckCircleOutlined, CloseCircleOutlined, CaretRightOutlined, ExclamationCircleOutlined, ClearOutlined, FileTextOutlined, SettingOutlined } from '@ant-design/icons';
import { FileClock, FileCheck, CircleX, Loader } from 'lucide-react';
import type { ColumnsType } from 'antd/es/table';
import InsuranceSmartSearch from '../components/InsuranceSmartSearch';
import { insuranceCarrierService } from '../modules/insurance/services/insuranceService';
import { InsuranceCarrier } from '../modules/insurance/types';
import '../styles/InsurancePage.css';
const BACKEND_URL = '/api/demographics';

const { Option } = Select;

// Sortable Item Component
interface SortableItemProps {
  id: string;
  label: string;
  checked: boolean;
  onToggle: (key: string, checked: boolean) => void;
}

const SortableItem: React.FC<SortableItemProps> = ({ id, label, checked, onToggle }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={{
        ...style,
        display: 'flex',
        alignItems: 'center',
        padding: '4px 8px',
        borderRadius: '4px',
        backgroundColor: isDragging ? '#f0f0f0' : 'transparent',
        border: isDragging ? '1px solid #d9d9d9' : '1px solid transparent',
        transition: 'all 0.2s ease',
      }}
    >
      <div 
        {...attributes}
        {...listeners}
        style={{ 
          marginRight: '8px',
          cursor: 'grab',
          color: '#8c8c8c',
          fontSize: '14px',
          padding: '4px',
          borderRadius: '4px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: '20px',
          height: '20px',
          backgroundColor: '#f5f5f5',
          border: '1px solid #e8e8e8'
        }}
      >
        ⋮⋮
      </div>
      <Checkbox
        checked={checked}
        onChange={(e) => {
          e.stopPropagation();
          onToggle(id, e.target.checked);
        }}
        style={{ marginRight: '8px' }}
        onClick={(e) => e.stopPropagation()}
      />
      <span 
        style={{ 
          fontSize: '13px',
          color: '#262626',
          cursor: 'pointer',
          userSelect: 'none',
          flex: 1
        }}
        onClick={(e) => {
          e.stopPropagation();
          onToggle(id, !checked);
        }}
      >
        {label}
      </span>
    </div>
  );
};

// Auth headers are now imported from utils/authHeaders

interface DemographicsData {
  id: string;
  doctorName: string;
  procedureName: string;
  firstName: string;
  middleName?: string;
  lastName: string;
  dateOfBirth?: string;
  surgeryDate: string;
  uploadedOn: string;
  lastModified: string;
  filename: string;
  contentType: string;
  onDemand: boolean;
  status: 'Pending' | 'In Progress' | 'Accepted' | 'Rejected';
  isDuplicate: boolean;
  rejectedBy?: string;
  rejectedReason?: string;
  insuranceCarrier?: string;
  waitingPeriod?: number;
}

interface Note {
  _id: string;
  orderId: string;
  text: string;
  addedBy: string;
  addedByRole?: string;
  createdAt: string;
}


interface Doctor {
  employeeId: string;
  name: string;
  specializations: string[];
}

interface FileUploadState {
  selectedFile: File | null;
  isUploading: boolean;
  uploadStatus: string;
  selectedDoctor: string;
  selectedProcedure: string;
  enableOnDemand: boolean;
  uploadError: string | null;
}

const InsurancePage: React.FC = () => {
  const { user } = useAuth();
  const [demographicsData, setDemographicsData] = useState<DemographicsData[]>([]);
  const [filteredData, setFilteredData] = useState<DemographicsData[]>([]);
  const [loading, setLoading] = useState(false);
  const [smartSearchTerm, setSmartSearchTerm] = useState('');
  const [smartSearchField, setSmartSearchField] = useState('all');
  const [totalCount, setTotalCount] = useState(0);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalKey, setModalKey] = useState(0); // Add this to force Upload component reset
  const [isResubmitModalVisible, setIsResubmitModalVisible] = useState(false);
  const [resubmitRecord, setResubmitRecord] = useState<DemographicsData | null>(null);
  const [resubmitFileState, setResubmitFileState] = useState<FileUploadState>({
    selectedFile: null,
    isUploading: false,
    uploadStatus: '',
    selectedDoctor: '',
    selectedProcedure: '',
    enableOnDemand: false,
    uploadError: null,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [updatingOnDemand, setUpdatingOnDemand] = useState<string | null>(null);
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [availableProcedures, setAvailableProcedures] = useState<string[]>([]);
  const [loadingDoctors, setLoadingDoctors] = useState(false);
  const [loadingProcedures, setLoadingProcedures] = useState(false);
  const [statusStats, setStatusStats] = useState({
    'Pending': 0,
    'In Progress': 0,
    'Accepted': 0,
    'Rejected': 0
  });
  const [selectedStatus, setSelectedStatus] = useState<string | null>('Pending'); // Initialize to Pending to filter by Pending status on load
  const [visualSelectedStatus, setVisualSelectedStatus] = useState<string | null>('Pending'); // Track which status box is visually selected
  const [showColumnsDropdown, setShowColumnsDropdown] = useState(false);
  const [isNotesModalVisible, setIsNotesModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<DemographicsData | null>(null);
  const [notes, setNotes] = useState<Note[]>([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [newNoteText, setNewNoteText] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [insuranceFileInfo, setInsuranceFileInfo] = useState<Record<string, any>>({});
  const [isInsuranceUploadModalVisible, setIsInsuranceUploadModalVisible] = useState(false);
  const [selectedRecordForInsurance, setSelectedRecordForInsurance] = useState<DemographicsData | null>(null);
  const [insuranceFileState, setInsuranceFileState] = useState<{
    selectedFile: File | null;
    isUploading: boolean;
    uploadError: string | null;
  }>({
    selectedFile: null,
    isUploading: false,
    uploadError: null
  });
  const [insuranceCarriers, setInsuranceCarriers] = useState<InsuranceCarrier[]>([]);
  const [loadingInsuranceCarriers, setLoadingInsuranceCarriers] = useState(false);
  const [isAcceptModalVisible, setIsAcceptModalVisible] = useState(false);
  const [selectedRecordForAccept, setSelectedRecordForAccept] = useState<DemographicsData | null>(null);
  const [selectedCarrier, setSelectedCarrier] = useState<string>('');
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({
    id: true,
    doctorName: true,
    patientName: true,
    dateOfBirth: true,
    procedureName: true,
    surgeryDate: true,
    uploadedOn: true,
    filename: true,
    onDemand: true,
    status: true,
    actions: true
  });
  const [columnOrder, setColumnOrder] = useState<string[]>([
    'id',
    'doctorName',
    'patientName',
    'dateOfBirth',
    'procedureName',
    'surgeryDate',
    'uploadedOn',
    'filename',
    'onDemand',
    'status',
    'actions'
  ]);

  // Table ref for clearing filters and sorting
  const tableRef = useRef<any>(null);

  // Key to force table re-render when clearing filters/sorting
  const [tableKey, setTableKey] = useState(0);


  const [fileState, setFileState] = useState<FileUploadState>({
    selectedFile: null,
    isUploading: false,
    uploadStatus: '',
    selectedDoctor: '',
    selectedProcedure: '',
    enableOnDemand: false,
    uploadError: null
  });


  // Fetch notes for a specific order
  const fetchNotes = async (orderId: string) => {
    setNotesLoading(true);
    try {
      const response = await fetch(`/api/notes/${orderId}`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setNotes(data.data || []);
      } else {
        message.error('Failed to fetch notes');
        setNotes([]);
      }
    } catch (error) {
      console.error('Error fetching notes:', error);
      message.error('Error fetching notes');
      setNotes([]);
    } finally {
      setNotesLoading(false);
    }
  };

  // Add a new note
  const addNote = async (orderId: string, text: string) => {
    setAddingNote(true);
    try {
      const response = await fetch('/api/notes', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ orderId, text })
      });
      if (response.ok) {
        message.success('Note added successfully');
        setNewNoteText('');
        // Refresh notes list
        await fetchNotes(orderId);
      } else {
        const errorData = await response.json();
        message.error(errorData.message || 'Failed to add note');
      }
    } catch (error) {
      console.error('Error adding note:', error);
      message.error('Error adding note');
    } finally {
      setAddingNote(false);
    }
  };

  // Delete a note
  const deleteNote = async (noteId: string) => {
    try {
      const response = await fetch(`/api/notes/${noteId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        message.success('Note deleted successfully');
        // Refresh notes list using selectedRecord.id (demographics sequential ID)
        if (selectedRecord) {
          await fetchNotes(selectedRecord.id);
        }
      } else {
        const errorData = await response.json();
        message.error(errorData.message || 'Failed to delete note');
      }
    } catch (error) {
      console.error('Error deleting note:', error);
      message.error('Error deleting note');
    }
  };

  // Fetch demographics data with server-side pagination
  const fetchDemographicsData = async (page: number = currentPage, limit: number = pageSize, search: string = '', searchField: string = 'all', status: string = '') => {
    setLoading(true);
    try {
      // Build query parameters
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        sortBy: 'uploadedOn',
        sortOrder: 'desc',
        isDuplicate: 'false' // Exclude duplicate records from the query
      });

      if (search) {
        params.append('search', search);
        params.append('searchField', searchField);
      }
      if (status) {
        params.append('status', status);
      }

      const response = await fetch(`${BACKEND_URL}?${params.toString()}`, {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        const demographics = data.data || [];
        const pagination = data.pagination || {};
        const stats = data.stats || {};

        // Update pagination info
        setTotalCount(pagination.totalCount || 0);

        // Update status stats from filtered results
        if (stats.statusCounts) {
          setStatusStats({
            'Pending': stats.statusCounts['Pending'] || 0,
            'In Progress': stats.statusCounts['In Progress'] || 0,
            'Accepted': stats.statusCounts['Accepted'] || 0,
            'Rejected': stats.statusCounts['Rejected'] || 0
          });
        }

        // Note: stats.totalCount is available if needed for future features

        // Apply client-side sorting for onDemand priority (since server doesn't handle this complex sorting)
        const sortedDemographics = [...demographics].sort((a: DemographicsData, b: DemographicsData) => {
          // First sort by onDemand (true values first)
          if (a.onDemand !== b.onDemand) {
            return b.onDemand ? 1 : -1;
          }
          // Then sort by timestamp (newest first) - this should already be handled by server
          return new Date(b.uploadedOn).getTime() - new Date(a.uploadedOn).getTime();
        });

        setDemographicsData(sortedDemographics);
        setFilteredData(sortedDemographics);
      } else {
        message.error('Failed to fetch demographics data');
      }
    } catch (error) {
      message.error('Error connecting to server');
    } finally {
      setLoading(false);
    }
  };

  // Note: Status statistics are now handled automatically in fetchDemographicsData() via the stats object from API response

  // Handle status filter click
  const handleStatusFilterClick = (status: string) => {
    setSelectedStatus(status);
    setVisualSelectedStatus(status);
    setCurrentPage(1); // Reset to first page
  };

  // Fetch doctors from database
  const fetchDoctors = async () => {
    setLoadingDoctors(true);
    try {
      const response = await fetch(`${BACKEND_URL}/doctors`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setDoctors(data.data || []);
      } else {
        message.error('Failed to fetch doctors');
      }
    } catch (error) {
      message.error('Error fetching doctors');
    } finally {
      setLoadingDoctors(false);
    }
  };

  // Fetch procedures by doctor
  const fetchProceduresByDoctor = async (doctorId: string) => {
    setLoadingProcedures(true);
    try {
      const response = await fetch(`${BACKEND_URL}/doctors/${doctorId}/procedures`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setAvailableProcedures(data.data || []);
      } else {
        message.error('Failed to fetch procedures');
        setAvailableProcedures([]);
      }
    } catch (error) {
      message.error('Error fetching procedures');
      setAvailableProcedures([]);
    } finally {
      setLoadingProcedures(false);
    }
  };

  // Fetch insurance carriers from database
  const fetchInsuranceCarriers = async () => {
    setLoadingInsuranceCarriers(true);
    try {
      const response = await insuranceCarrierService.getAll({ isActive: true });
      setInsuranceCarriers(response.data || []);
    } catch (error) {
      console.error('Error fetching insurance carriers:', error);
      message.error('Error fetching insurance carriers');
    } finally {
      setLoadingInsuranceCarriers(false);
    }
  };

  useEffect(() => {
    fetchDoctors(); // Fetch doctors on component mount
    fetchInsuranceCarriers(); // Fetch insurance carriers on component mount
    // fetchStatusStats() and fetchTotalCountStats() are now handled automatically in fetchDemographicsData()
  }, []);

  // Fetch insurance info for In Progress, Accepted, and Rejected records
  useEffect(() => {
    const recordsWithInsurance = demographicsData.filter(record => 
      record.status === 'In Progress' || record.status === 'Accepted' || record.status === 'Rejected'
    );
    recordsWithInsurance.forEach(record => {
      fetchInsuranceInfo(record.id);
    });
  }, [demographicsData]);

  // Handle click outside to close columns dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // Check if click is outside the columns dropdown and button
      if (showColumnsDropdown && 
          !target.closest('[data-columns-dropdown]') && 
          !target.closest('[data-columns-button]')) {
        setShowColumnsDropdown(false);
      }
    };

    if (showColumnsDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showColumnsDropdown]);

  // Handle smart search
  const handleSmartSearch = (searchTerm: string, selectedField: string = 'all') => {
    console.log('🔄 handleSmartSearch: called with searchTerm:', searchTerm, 'selectedField:', selectedField, 'current smartSearchTerm:', smartSearchTerm);
    
    // Only reset to first page if the search term has actually changed
    const hasSearchTermChanged = searchTerm !== smartSearchTerm;
    const isNewSearch = searchTerm.trim() !== '' && hasSearchTermChanged;
    
    setSmartSearchTerm(searchTerm);
    setSmartSearchField(selectedField);
    
    if (isNewSearch) {
      console.log('🔄 handleSmartSearch: setCurrentPage(1) called because searchTerm changed to:', searchTerm);
      setCurrentPage(1); // Reset to first page when searching
    } else {
      console.log('🔄 handleSmartSearch: NOT resetting page because searchTerm unchanged or empty');
    }
  };

  // Update filteredData when demographicsData changes (for server-side pagination)
  // Backend now handles duplicate filtering via isDuplicate=false parameter
  useEffect(() => {
    setFilteredData(demographicsData);
  }, [demographicsData]);

  // Fetch paginated data when filters or pagination change
  useEffect(() => {
    fetchDemographicsData(currentPage, pageSize, smartSearchTerm, smartSearchField, selectedStatus || '');
  }, [currentPage, pageSize, smartSearchTerm, smartSearchField, selectedStatus]);

  // Handle PDF viewing
  const handleViewPDF = (id: string) => {
    const viewerUrl = `/view-pdf/demographics/${id}`;
    window.open(viewerUrl, '_blank');
  };








  // Handle clear table filters and sorting only
  const handleClearTableFilters = () => {
    // Force table re-render to clear all filters and sorting state completely
    setTableKey(prev => prev + 1);

    message.success('Table filters and sorting cleared');
  };

  // Handle column visibility change
  const handleColumnVisibilityChange = (columnKey: string, checked: boolean) => {
    setColumnVisibility(prev => ({
      ...prev,
      [columnKey]: checked
    }));
  };

  // Column configuration with display names
  const columnConfigMap = {
    id: 'Verification ID',
    doctorName: 'Doctor Name',
    patientName: 'Patient Name',
    dateOfBirth: 'Date of Birth',
    procedureName: 'Procedure',
    surgeryDate: 'Surgery Date',
    uploadedOn: 'Uploaded On',
    filename: 'Filename',
    onDemand: 'On Demand',
    status: 'Status',
    actions: 'Actions'
  };

  // Get ordered column config based on columnOrder
  const getOrderedColumnConfig = () => {
    return columnOrder.map(key => ({
      key,
      label: columnConfigMap[key as keyof typeof columnConfigMap]
    }));
  };

  // Handle drag end for column reordering
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setColumnOrder((items) => {
        const oldIndex = items.indexOf(active.id as string);
        const newIndex = items.indexOf(over.id as string);

        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  // Sensors for drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Filter and order columns based on visibility state and column order
  const getVisibleColumns = (originalColumns: ColumnsType<DemographicsData>) => {
    // First filter by visibility
    const visibleColumns = originalColumns.filter(column => {
      const columnKey = column.key as string;
      return columnVisibility[columnKey] !== false;
    });

    // Then sort by column order
    return visibleColumns.sort((a, b) => {
      const indexA = columnOrder.indexOf(a.key as string);
      const indexB = columnOrder.indexOf(b.key as string);
      return indexA - indexB;
    });
  };


  // Handle start verification
  const handleStartVerification = async (id: string) => {
    try {
      const response = await fetch(`${BACKEND_URL}/${id}/start-verification`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        message.success('Verification started successfully');
        fetchDemographicsData(currentPage, pageSize, smartSearchTerm, smartSearchField, selectedStatus || ''); // Refresh data and stats
      } else {
        const errorData = await response.json();
        message.error(errorData.message || 'Failed to start verification');
      }
    } catch (error) {
      message.error('Error starting verification');
    }
  };

  // Fetch insurance file info for a record
  const fetchInsuranceInfo = async (demographicsId: string) => {
    try {
      const response = await fetch(`/api/insurance/verifications/${demographicsId}/info`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setInsuranceFileInfo(prev => ({
          ...prev,
          [demographicsId]: data
        }));
      }
    } catch (error) {
      console.error('Error fetching insurance info:', error);
    }
  };

  // Handle insurance file upload
  const handleInsuranceFileUpload = async () => {
    if (!selectedRecordForInsurance || !insuranceFileState.selectedFile) {
      message.error('Please select a file to upload');
      return;
    }

    setInsuranceFileState(prev => ({ ...prev, isUploading: true, uploadError: null }));

    const formData = new FormData();
    formData.append('insuranceFile', insuranceFileState.selectedFile);

    try {
      const response = await fetch(`/api/insurance/verifications/${selectedRecordForInsurance.id}/upload`, {
        method: 'POST',
        headers: getAuthHeadersForUpload(),
        body: formData,
      });

      if (response.ok) {
        message.success('Insurance file uploaded successfully');
        setIsInsuranceUploadModalVisible(false);
        setInsuranceFileState({
          selectedFile: null,
          isUploading: false,
          uploadError: null
        });
        setSelectedRecordForInsurance(null);
        // Refresh insurance info for this record
        await fetchInsuranceInfo(selectedRecordForInsurance.id);
      } else {
        const errorData = await response.json();
        message.error(errorData.message || 'Failed to upload insurance file');
        setInsuranceFileState(prev => ({ ...prev, uploadError: errorData.message }));
      }
    } catch (error) {
      message.error('Error uploading insurance file');
      setInsuranceFileState(prev => ({ ...prev, uploadError: 'Network error' }));
    } finally {
      setInsuranceFileState(prev => ({ ...prev, isUploading: false }));
    }
  };

  // Handle accept insurance with carrier selection
  const handleAcceptInsurance = async (id: string, insuranceCarrierId: string) => {
    try {
      const response = await fetch(`/api/insurance/verifications/${id}/accept`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          acceptedBy: 'Insurance Verifier', // You can get this from user context
          insuranceCarrierId: insuranceCarrierId
        })
      });

      if (response.ok) {
        message.success('Insurance verification accepted successfully');
        fetchDemographicsData(currentPage, pageSize, smartSearchTerm, smartSearchField, selectedStatus || ''); // Refresh data and stats
        // Close the modal and reset state
        setIsAcceptModalVisible(false);
        setSelectedRecordForAccept(null);
        setSelectedCarrier('');
      } else {
        const errorData = await response.json();
        message.error(errorData.error || errorData.message || 'Failed to accept insurance verification');
      }
    } catch (error) {
      message.error('Error accepting insurance verification');
    }
  };

  // Handle opening accept modal
  const handleOpenAcceptModal = (record: DemographicsData) => {
    setSelectedRecordForAccept(record);
    setIsAcceptModalVisible(true);
    setSelectedCarrier('');
  };

  // Handle accept modal submit
  const handleAcceptModalSubmit = () => {
    if (!selectedCarrier) {
      message.error('Please select an insurance carrier');
      return;
    }
    if (selectedRecordForAccept) {
      handleAcceptInsurance(selectedRecordForAccept.id, selectedCarrier);
    }
  };

  // Handle reject insurance
  const handleRejectInsurance = async (id: string) => {
    Modal.confirm({
      title: 'Reject Insurance Verification',
      content: (
        <div>
          <p>Are you sure you want to reject this insurance verification?</p>
          <Input.TextArea
            placeholder="Please provide a reason for rejection..."
            id="rejection-reason"
            rows={3}
          />
        </div>
      ),
      okText: 'Reject',
      cancelText: 'Cancel',
      onOk: async () => {
        const reason = (document.getElementById('rejection-reason') as HTMLTextAreaElement)?.value || '';
        
        try {
          const response = await fetch(`/api/insurance/verifications/${id}/reject`, {
            method: 'PATCH',
            headers: getAuthHeaders(),
            body: JSON.stringify({
              rejectedBy: 'Insurance Verifier', // You can get this from user context
              rejectedReason: reason
            })
          });

          if (response.ok) {
            message.success('Insurance verification rejected successfully');
            fetchDemographicsData(currentPage, pageSize, smartSearchTerm, smartSearchField, selectedStatus || ''); // Refresh data and stats
          } else {
            const errorData = await response.json();
            message.error(errorData.message || 'Failed to reject insurance verification');
          }
        } catch (error) {
          message.error('Error rejecting insurance verification');
        }
      }
    });
  };

  // Handle view insurance file
  const handleViewInsuranceFile = (id: string) => {
    const viewerUrl = `/view-pdf/insurance/${id}`;
    window.open(viewerUrl, '_blank');
  };


  // Table columns configuration for regular demographics
  const columns: ColumnsType<DemographicsData> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      sorter: (a, b) => Number(a.id) - Number(b.id),
    },
    {
      title: 'Doctor Name',
      dataIndex: 'doctorName',
      key: 'doctorName',
      width: 200,
      sorter: (a, b) => a.doctorName.localeCompare(b.doctorName),
      filters: Array.from(new Set(demographicsData.map(item => item.doctorName))).map(name => ({
        text: name,
        value: name,
      })),
      onFilter: (value, record) => record.doctorName === value,
    },
    {
      title: 'Patient Name',
      key: 'patientName',
      width: 200,
      render: (_, record) => {
        const fullName = [record.firstName, record.middleName, record.lastName]
          .filter(name => name && name.trim())
          .join(' ');
        return fullName || 'No name provided';
      },
      sorter: (a, b) => {
        const nameA = [a.firstName, a.middleName, a.lastName].filter(name => name && name.trim()).join(' ');
        const nameB = [b.firstName, b.middleName, b.lastName].filter(name => name && name.trim()).join(' ');
        return nameA.localeCompare(nameB);
      },
    },
    {
      title: 'Date of Birth',
      dataIndex: 'dateOfBirth',
      key: 'dateOfBirth',
      width: 120,
      render: (date) => {
        return (
          <span>
            {date ? new Date(date).toLocaleDateString() : 'Not set'}
          </span>
        );
      },
      sorter: (a, b) => {
        const dateA = a.dateOfBirth ? new Date(a.dateOfBirth).getTime() : 0;
        const dateB = b.dateOfBirth ? new Date(b.dateOfBirth).getTime() : 0;
        return dateA - dateB;
      },
    },
    {
      title: 'Procedure',
      dataIndex: 'procedureName',
      key: 'procedureName',
      width: 180,
      sorter: (a, b) => a.procedureName.localeCompare(b.procedureName),
      filters: Array.from(new Set(demographicsData.map(item => item.procedureName))).map(name => ({
        text: name,
        value: name,
      })),
      onFilter: (value, record) => record.procedureName === value,
    },
     {
              title: 'Surgery Date',
        dataIndex: 'surgeryDate',
        key: 'surgeryDate',
        width: 130,
        render: (date, record) => {
          // Surgery Date is always read-only (view-only) in Insurance Page
          return (
            <span style={{ color: '#666', fontStyle: 'italic' }}>
              {date ? new Date(date).toLocaleDateString() : 'Not set'}
            </span>
          );
        },
        sorter: (a, b) => new Date(a.surgeryDate).getTime() - new Date(b.surgeryDate).getTime(),
     },
    {
      title: 'Uploaded On',
      dataIndex: 'uploadedOn',
      key: 'uploadedOn',
      width: 120,
      render: (date) => {
        return (
          <span>
            {date ? new Date(date).toLocaleDateString() : 'Not set'}
          </span>
        );
      },
      sorter: (a, b) => new Date(a.uploadedOn).getTime() - new Date(b.uploadedOn).getTime(),
    },
    {
      title: 'Filename',
      dataIndex: 'filename',
      key: 'filename',
      width: 200,
      ellipsis: true,
      render: (filename, record) => {
        if (!filename) {
          return <span style={{ color: '#999' }}>No file</span>;
        }
        return (
          <Button
            type="link"
            onClick={() => handleViewPDF(record.id)}
            style={{ 
              padding: 0, 
              height: 'auto',
              textAlign: 'left',
              width: '100%',
              justifyContent: 'flex-start'
            }}
          >
            {filename}
          </Button>
        );
      },
      sorter: (a, b) => (a.filename || '').localeCompare(b.filename || ''),
    },
    {
      title: 'On Demand',
      dataIndex: 'onDemand',
      key: 'onDemand',
      width: 120,
      render: (onDemand, record) => {
        return (
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            padding: '6px 10px',
            borderRadius: '8px',
            minHeight: '32px'
          }}>
            <Switch
              checked={onDemand}
              disabled={true}
              size="small"
            />
          </div>
        );
      },
      filters: [
        { text: 'Yes', value: true },
        { text: 'No', value: false },
      ],
      onFilter: (value, record) => record.onDemand === value,
      // Remove default sorter to maintain our custom sorting
      sorter: false,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 140,
      render: (status) => {
        const statusConfig = {
          'Pending': { color: '#faad14', icon: <ClockCircleOutlined />, text: 'Pending' },
          'In Progress': { color: '#1890ff', icon: <ExclamationCircleOutlined />, text: 'In Progress' },
          'Accepted': { color: '#52c41a', icon: <CheckCircleOutlined />, text: 'Accepted' },
          'Rejected': { color: '#ff4d4f', icon: <CloseCircleOutlined />, text: 'Rejected' }
        };

        const config = statusConfig[status as keyof typeof statusConfig];

        return (
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            padding: '6px 10px',
            borderRadius: '8px',
            minHeight: '32px'
          }}>
            <Tag color={config.color} style={{ margin: 0 }}>
              {config.icon}
              <span style={{ marginLeft: '4px' }}>{config.text}</span>
            </Tag>
          </div>
        );
      },
      filters: [
        { text: 'Pending', value: 'Pending' },
        { text: 'In Progress', value: 'In Progress' },
        { text: 'Accepted', value: 'Accepted' },
        { text: 'Rejected', value: 'Rejected' },
      ],
      onFilter: (value, record) => record.status === value,
      sorter: (a, b) => a.status.localeCompare(b.status),
    },
    // Insurance Carrier - For Accepted and Rejected statuses
    ...(['Accepted', 'Rejected'].includes(selectedStatus || '') ? [{
      title: 'Insurance Carrier',
      dataIndex: 'insuranceCarrier',
      key: 'insuranceCarrier',
      width: 150,
      render: (insuranceCarrier: string, record: DemographicsData) => {
        if (!insuranceCarrier || insuranceCarrier.trim() === '') {
          return <span style={{ color: '#999', fontStyle: 'italic' }}>Not set</span>;
        }
        return (
          <span style={{ color: '#1890ff', fontWeight: '500' }}>
            {insuranceCarrier}
          </span>
        );
      },
      sorter: (a: any, b: any) => {
        const aCarrier = a.insuranceCarrier || '';
        const bCarrier = b.insuranceCarrier || '';
        return aCarrier.localeCompare(bCarrier);
      },
      filters: Array.from(new Set(demographicsData
        .filter(item => ['Accepted', 'Rejected'].includes(item.status))
        .filter(item => item.insuranceCarrier && item.insuranceCarrier.trim() !== '')
        .map(item => item.insuranceCarrier)
      )).filter((carrier): carrier is string => carrier !== undefined && carrier !== null)
        .map(carrier => ({
          text: carrier,
          value: carrier,
        })),
      onFilter: (value: any, record: any) => record.insuranceCarrier === value,
    }] : []),
         {
       title: 'Actions',
       key: 'actions',
       width: 200,
       render: (_, record) => {
         // Disable editing and deletion for Accepted, Rejected, and In Progress records
         const isEditable = record.status === 'Pending';

         return (
           <div style={{ display: 'flex', gap: '4px' }}>
             
             {/* Start Verification Button - Only for Pending records */}
             {record.status === 'Pending' && (
               <Button
                 type="primary"
                 size="small"
                 icon={<CaretRightOutlined />}
                 onClick={() => {
                   Modal.confirm({
                     title: 'Start Verification',
                     content: 'Are you sure you want to start verification for this record? This will change the status to "In Progress".',
                     okText: 'Yes, Start Verification',
                     cancelText: 'Cancel',
                     onOk: () => handleStartVerification(record.id)
                   });
                 }}
                 style={{ 
                   padding: '4px 8px',
                   minWidth: '32px',
                   height: '32px',
                   display: 'flex',
                   alignItems: 'center',
                   justifyContent: 'center',
                   backgroundColor: '#1890ff',
                   borderColor: '#1890ff'
                 }}
                 title="Start Verification"
               />
             )}

             {/* Order Notes Button - For Pending records */}
             {record.status === 'Pending' && (
               <Button
                 type="default"
                 size="small"
                 icon={<FileTextOutlined />}
                 onClick={() => handleOpenNotesModal(record)}
                 style={{ 
                   padding: '4px 8px',
                   minWidth: '32px',
                   height: '32px',
                   display: 'flex',
                   alignItems: 'center',
                   justifyContent: 'center'
                 }}
                 title="Order Notes"
               />
             )}

             {/* Insurance Verification Actions - Only for In Progress records */}
             {record.status === 'In Progress' && (
               <>
                 {/* Upload Insurance File Button */}
                 <Button
                   type="default"
                   size="small"
                   icon={<UploadOutlined />}
                   onClick={() => {
                     setSelectedRecordForInsurance(record);
                     setIsInsuranceUploadModalVisible(true);
                   }}
                   style={{ 
                     padding: '4px 8px',
                     minWidth: '32px',
                     height: '32px',
                     display: 'flex',
                     alignItems: 'center',
                     justifyContent: 'center'
                   }}
                   title="Upload Insurance File"
                 />

                 {/* Accept Button - Always visible */}
                 <Button
                   type="primary"
                   size="small"
                   icon={<CheckCircleOutlined />}
                   onClick={() => handleOpenAcceptModal(record)}
                   style={{ 
                     padding: '4px 8px',
                     minWidth: '32px',
                     height: '32px',
                     display: 'flex',
                     alignItems: 'center',
                     justifyContent: 'center',
                     backgroundColor: '#52c41a',
                     borderColor: '#52c41a'
                   }}
                   title="Accept"
                 />

                 {/* Reject Button - Always visible */}
                 <Button
                   type="primary"
                   size="small"
                   icon={<CloseCircleOutlined />}
                   onClick={() => handleRejectInsurance(record.id)}
                   style={{ 
                     padding: '4px 8px',
                     minWidth: '32px',
                     height: '32px',
                     display: 'flex',
                     alignItems: 'center',
                     justifyContent: 'center',
                     backgroundColor: '#ff4d4f',
                     borderColor: '#ff4d4f'
                   }}
                   title="Reject"
                 />

                 {/* View Insurance File Button - Only show if file exists */}
                 {insuranceFileInfo[record.id]?.hasInsuranceFile && (
                   <Button
                     type="text"
                     size="small"
                     icon={<FileTextOutlined />}
                     onClick={() => handleViewInsuranceFile(record.id)}
                     style={{ 
                       padding: '4px 8px',
                       minWidth: '32px',
                       height: '32px',
                       display: 'flex',
                       alignItems: 'center',
                       justifyContent: 'center',
                       color: '#1890ff'
                     }}
                     title="View Insurance File"
                   />
                 )}

                 {/* Order Notes Button - For In Progress records */}
                 <Button
                   type="default"
                   size="small"
                   icon={<FileTextOutlined />}
                   onClick={() => handleOpenNotesModal(record)}
                   style={{ 
                     padding: '4px 8px',
                     minWidth: '32px',
                     height: '32px',
                     display: 'flex',
                     alignItems: 'center',
                     justifyContent: 'center'
                   }}
                   title="Order Notes"
                 />
               </>
             )}

             {/* View Insurance File Button - For Accepted and Rejected records */}
             {(record.status === 'Accepted' || record.status === 'Rejected') && (
               <>
                 <Button
                   type="text"
                   size="small"
                   icon={<FileTextOutlined />}
                   onClick={() => handleViewInsuranceFile(record.id)}
                   style={{ 
                     padding: '4px 8px',
                     minWidth: '32px',
                     height: '32px',
                     display: 'flex',
                     alignItems: 'center',
                     justifyContent: 'center',
                     color: '#1890ff'
                   }}
                   title="View Insurance File"
                 />

                 {/* Order Notes Button - For Accepted and Rejected records */}
                 <Button
                   type="default"
                   size="small"
                   icon={<FileTextOutlined />}
                   onClick={() => handleOpenNotesModal(record)}
                   style={{ 
                     padding: '4px 8px',
                     minWidth: '32px',
                     height: '32px',
                     display: 'flex',
                     alignItems: 'center',
                     justifyContent: 'center'
                   }}
                   title="Order Notes"
                 />
               </>
             )}
           </div>
         );
       },
     },
  ];

  // Columns for rejected records
  const rejectedColumns: ColumnsType<DemographicsData> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      sorter: (a, b) => Number(a.id) - Number(b.id),
    },
    {
      title: 'Rejected Reason',
      dataIndex: 'rejectedReason',
      key: 'rejectedReason',
      width: 200,
      render: (text) => text || 'No reason provided',
      ellipsis: true,
    },
    {
      title: 'Patient Name',
      key: 'patientName',
      width: 80,
      render: (_, record) => {
        const fullName = [record.firstName, record.middleName, record.lastName]
          .filter(name => name && name.trim())
          .join(' ');
        return fullName || 'No name provided';
      },
      sorter: (a, b) => {
        const nameA = [a.firstName, a.middleName, a.lastName].filter(name => name && name.trim()).join(' ');
        const nameB = [b.firstName, b.middleName, b.lastName].filter(name => name && name.trim()).join(' ');
        return nameA.localeCompare(nameB);
      },
    },
    {
      title: 'Rejected By',
      dataIndex: 'rejectedBy',
      key: 'rejectedBy',
      width: 100,
      render: (text) => text || 'Unknown',
    },
    {
      title: 'Filename',
      dataIndex: 'filename',
      key: 'filename',
      width: 100,
      ellipsis: true,
      render: (text, record) => (
        <Button
          type="link"
          onClick={() => handleViewPDF(record.id)}
          style={{ padding: 0, height: 'auto' }}
        >
          {text}
        </Button>
      ),
    },
    {
      title: 'Insurance Carrier',
      dataIndex: 'insuranceCarrier',
      key: 'insuranceCarrier',
      width: 150,
      render: (insuranceCarrier: string, record: DemographicsData) => {
        if (!insuranceCarrier || insuranceCarrier.trim() === '') {
          return <span style={{ color: '#999', fontStyle: 'italic' }}>Not set</span>;
        }
        return (
          <span style={{ color: '#1890ff', fontWeight: '500' }}>
            {insuranceCarrier}
          </span>
        );
      },
      sorter: (a: any, b: any) => {
        const aCarrier = a.insuranceCarrier || '';
        const bCarrier = b.insuranceCarrier || '';
        return aCarrier.localeCompare(bCarrier);
      },
      filters: Array.from(new Set(demographicsData
        .filter(item => item.status === 'Rejected')
        .filter(item => item.insuranceCarrier && item.insuranceCarrier.trim() !== '')
        .map(item => item.insuranceCarrier)
      )).filter((carrier): carrier is string => carrier !== undefined && carrier !== null)
        .map(carrier => ({
          text: carrier,
          value: carrier,
        })),
      onFilter: (value: any, record: any) => record.insuranceCarrier === value,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 80,
      render: (_, record) => (
        <div style={{ display: 'flex', gap: '4px' }}>
          <Button
            type="text"
            size="small"
            icon={<FileTextOutlined />}
            onClick={() => handleViewInsuranceFile(record.id)}
            style={{ 
              padding: '4px 8px',
              minWidth: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#1890ff'
            }}
            title="View Insurance File"
          />
          <Button
            type="text"
            size="small"
            icon={<FileTextOutlined />}
            onClick={() => handleOpenNotesModal(record)}
            style={{ 
              padding: '4px 8px',
              minWidth: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#722ed1'
            }}
            title="Order Notes"
          />
        </div>
      ),
    },
  ];

  // Modal form handlers
  const handleFileChange = (info: any) => {
    const file = info.file;
    if (file) {
      const allowedTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'text/csv'];
      if (allowedTypes.includes(file.type)) {
        setFileState(prev => ({ ...prev, selectedFile: file, uploadStatus: '', uploadError: null }));
      } else {
        message.error('Invalid file type. Only PDF, Excel, and CSV files are allowed.');
        setFileState(prev => ({ ...prev, selectedFile: null, uploadError: null }));
      }
    }
  };

  const handleDoctorChange = (value: string) => {
    setFileState(prev => ({ ...prev, selectedDoctor: value, selectedProcedure: '', uploadError: null }));
    // Fetch procedures for the selected doctor
    if (value) {
      fetchProceduresByDoctor(value);
    } else {
      setAvailableProcedures([]);
    }
  };

  const handleProcedureChange = (value: string) => {
    setFileState(prev => ({ ...prev, selectedProcedure: value, uploadError: null }));
  };

  const handleToggleChange = (checked: boolean) => {
    setFileState(prev => ({ ...prev, enableOnDemand: checked, uploadError: null }));
  };

  const handleModalSubmit = async () => {
    if (!fileState.selectedFile) {
      message.error('Please select a file first.');
      return;
    }

    if (!fileState.selectedDoctor) {
      message.error('Please select a doctor.');
      return;
    }

    if (!fileState.selectedProcedure) {
      message.error('Please select a procedure.');
      return;
    }

    setFileState(prev => ({ ...prev, isUploading: true, uploadError: null }));

    // Get the doctor name for display
    const selectedDoctor = doctors.find(d => d.employeeId === fileState.selectedDoctor);
    const doctorName = selectedDoctor ? selectedDoctor.name : fileState.selectedDoctor;

    const formData = new FormData();
    formData.append('patientDetails', fileState.selectedFile);
    formData.append('doctor', doctorName); // Send doctor name for backward compatibility
    formData.append('doctorId', fileState.selectedDoctor); // Send doctor ID for future use
    formData.append('procedure', fileState.selectedProcedure);
    formData.append('enableOnDemand', fileState.enableOnDemand.toString());

    try {
      const token = getAuthToken();
      const response = await fetch(`${BACKEND_URL}/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData,
      });

             if (response.ok) {
         let responseData;
         try {
           responseData = await response.json();
         } catch (parseError) {
           // If response is not JSON, treat it as plain text success
           responseData = { message: 'File uploaded successfully', isDuplicate: false };
         }

         if (responseData.isDuplicate || responseData.warning) {
           // Show warning for duplicates or surgery date issues
           message.warning(responseData.warning || 'Potential Duplicate Found, Please check the duplicates tab.');
         } else {
           message.success(responseData.message || 'File uploaded successfully!');
         }

         setFileState({
           selectedFile: null,
           isUploading: false,
           uploadStatus: '',
           selectedDoctor: '',
           selectedProcedure: '',
           enableOnDemand: false,
           uploadError: null
         });
         setIsModalVisible(false);
         fetchDemographicsData(currentPage, pageSize, smartSearchTerm, smartSearchField, selectedStatus || ''); // Refresh data and stats
               } else {
          // Handle backend errors properly
          let errorMessage = 'Upload failed. Please try again.';
          let validationErrors: string[] = [];

          try {
            const errorData = await response.json();
            errorMessage = errorData.message || errorData.error || errorMessage;

            // Check if there are validation errors
            if (errorData.validationErrors && Array.isArray(errorData.validationErrors)) {
              validationErrors = errorData.validationErrors;
            }
          } catch (parseError) {
            // If error response is not JSON, try to get text
            try {
              const errorText = await response.text();
              errorMessage = errorText || errorMessage;
            } catch (textError) {
              // If all else fails, use default message
              console.error('Could not parse error response:', textError);
            }
          }

          // Display validation errors if they exist
          if (validationErrors.length > 0) {
            const validationMessage = `File validation failed:\n${validationErrors.map(error => `• ${error}`).join('\n')}`;
            message.error({
              content: validationMessage,
              duration: 8, // Show longer for validation errors
              style: { whiteSpace: 'pre-line' }
            });
            errorMessage = validationMessage;
          } else {
            message.error(errorMessage);
          }

          // Set error state for display in modal
          setFileState(prev => ({ ...prev, uploadError: errorMessage }));
        }
    } catch (error) {
      console.error('Upload error:', error);
      if (error instanceof Error) {
        message.error(`Upload failed: ${error.message}`);
      } else {
        message.error('Upload failed. Please check your connection.');
      }
    } finally {
      setFileState(prev => ({ ...prev, isUploading: false }));
    }
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    clearFileState();
  };

  const clearFileState = () => {
    setFileState({
      selectedFile: null,
      isUploading: false,
      uploadStatus: '',
      selectedDoctor: '',
      selectedProcedure: '',
      enableOnDemand: false,
      uploadError: null
    });
  };

  const clearResubmitFileState = () => {
    setResubmitFileState({
      selectedFile: null,
      isUploading: false,
      uploadStatus: '',
      selectedDoctor: '',
      selectedProcedure: '',
      enableOnDemand: false,
      uploadError: null
    });
  };

  // Handle resubmit modal submit
  const handleResubmitSubmit = async () => {
    if (!resubmitRecord) return;

    if (!resubmitFileState.selectedDoctor) {
      message.error('Please select a doctor.');
      return;
    }

    if (!resubmitFileState.selectedProcedure) {
      message.error('Please select a procedure.');
      return;
    }

    setResubmitFileState(prev => ({ ...prev, isUploading: true, uploadError: null }));

    // Get the doctor name for display
    const selectedDoctor = doctors.find(d => d.employeeId === resubmitFileState.selectedDoctor);
    const doctorName = selectedDoctor ? selectedDoctor.name : resubmitFileState.selectedDoctor;

    const formData = new FormData();

    // Only append file if a new one is selected
    if (resubmitFileState.selectedFile) {
      formData.append('patientDetails', resubmitFileState.selectedFile);
    }

    formData.append('doctor', doctorName);
    formData.append('doctorId', resubmitFileState.selectedDoctor);
    formData.append('procedure', resubmitFileState.selectedProcedure);
    formData.append('enableOnDemand', resubmitFileState.enableOnDemand.toString());
    formData.append('keepExistingFile', (!resubmitFileState.selectedFile).toString());

    try {
      const token = getAuthToken();
      const response = await fetch(`${BACKEND_URL}/resubmit/${resubmitRecord.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData,
      });

      if (response.ok) {
        let responseData;
        try {
          responseData = await response.json();
        } catch (parseError) {
          responseData = { message: 'Record resubmitted successfully', isDuplicate: false };
        }

        if (responseData.isDuplicate || responseData.warning) {
          message.warning(responseData.warning || 'Potential Duplicate Found, Please check the duplicates tab.');
        } else {
          message.success('Record resubmitted successfully!');
        }

        clearResubmitFileState();
        setIsResubmitModalVisible(false);
        setResubmitRecord(null);
        fetchDemographicsData(currentPage, pageSize, smartSearchTerm, smartSearchField, selectedStatus || ''); // Refresh data and stats
      } else {
        let errorMessage = 'Resubmit failed. Please try again.';
        let validationErrors: string[] = [];

        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.error || errorMessage;
          validationErrors = errorData.errors || [];
        } catch (parseError) {
          // If response is not JSON, use the status text
          errorMessage = response.statusText || errorMessage;
        }

        if (validationErrors.length > 0) {
          message.error(
            <div>
              <div>{errorMessage}</div>
              <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                {validationErrors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          );
        } else {
          message.error(errorMessage);
        }
      }
    } catch (error) {
      console.error('Resubmit error:', error);
      message.error('Network error. Please check your connection and try again.');
    } finally {
      setResubmitFileState(prev => ({ ...prev, isUploading: false }));
    }
  };

  // Handle resubmit modal cancel
  const handleResubmitCancel = () => {
    setIsResubmitModalVisible(false);
    clearResubmitFileState();
    setResubmitRecord(null);
  };

  // Handle opening notes modal
  const handleOpenNotesModal = (record: DemographicsData) => {
    setSelectedRecord(record);
    setIsNotesModalVisible(true);
    setNewNoteText('');
    fetchNotes(record.id);
  };

  // Handle closing notes modal
  const handleCloseNotesModal = () => {
    setIsNotesModalVisible(false);
    setSelectedRecord(null);
    setNotes([]);
    setNewNoteText('');
  };

  // Handle adding a new note
  const handleAddNote = async () => {
    if (!newNoteText.trim() || !selectedRecord) return;
    await addNote(selectedRecord.id, newNoteText.trim());
  };

  // Handle note text area key press
  const handleNoteKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAddNote();
    }
  };

  // Check if notes should be read-only
  const isNotesReadOnly = (record: DemographicsData) => {
    return false; // Allow notes to be added/edited for all statuses in Insurance page
  };

  // Check if user can delete a specific note
  const canDeleteNote = (note: Note) => {
    // User can delete if they created the note, or if they're an admin
    return note.addedBy === user?.email || note.addedBy === user?.name || user?.role === 'admin';
  };

  return (
    <div className="demographics-page">
             {/* First Container - Search and Add Button (15% height) */}
       <div className="search-container">
         <div className="search-content" style={{ position: 'relative', zIndex: 1 }}>
           <div className="search-inputs" style={{ pointerEvents: 'auto', zIndex: 5, flex: 1 }}>
             <InsuranceSmartSearch
               onSearch={handleSmartSearch}
               style={{ marginBottom: 16, pointerEvents: 'auto' }}
             />

           </div>
         </div>
       </div>

      {/* Status Statistics Container */}
      <div className="status-stats-container" style={{ 
        padding: '16px', 
        backgroundColor: '#f5f5f5', 
        marginBottom: '16px',
        borderRadius: '8px'
      }}>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
          gap: '16px' 
        }}>

          {/* Pending Status Box */}
          <Card
            hoverable
            onClick={() => handleStatusFilterClick('Pending')}
            style={{
              cursor: 'pointer',
              border: visualSelectedStatus === 'Pending' ? '2px solid #faad14' : '1px solid #d9d9d9',
              backgroundColor: visualSelectedStatus === 'Pending' ? '#fffbe6' : '#fff'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ 
                width: '40px', 
                height: '40px', 
                borderRadius: '50%', 
                backgroundColor: '#faad14',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '16px'
              }}>
                <FileClock size={20} />
              </div>
              <div>
                <div style={{ fontWeight: 'bold', fontSize: '14px' }}>Pending</div>
                <div style={{ color: '#666', fontSize: '12px' }}>{statusStats['Pending']} records</div>
              </div>
            </div>
          </Card>

          {/* In Progress Status Box */}
          <Card
            hoverable
            onClick={() => handleStatusFilterClick('In Progress')}
            style={{
              cursor: 'pointer',
              border: visualSelectedStatus === 'In Progress' ? '2px solid #1890ff' : '1px solid #d9d9d9',
              backgroundColor: visualSelectedStatus === 'In Progress' ? '#e6f7ff' : '#fff'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ 
                width: '40px', 
                height: '40px', 
                borderRadius: '50%', 
                backgroundColor: '#1890ff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '16px'
              }}>
                <Loader size={20} />
              </div>
              <div>
                <div style={{ fontWeight: 'bold', fontSize: '14px' }}>In Progress</div>
                <div style={{ color: '#666', fontSize: '12px' }}>{statusStats['In Progress']} records</div>
              </div>
            </div>
          </Card>

          {/* Accepted Status Box */}
          <Card
            hoverable
            onClick={() => handleStatusFilterClick('Accepted')}
            style={{
              cursor: 'pointer',
              border: visualSelectedStatus === 'Accepted' ? '2px solid #52c41a' : '1px solid #d9d9d9',
              backgroundColor: visualSelectedStatus === 'Accepted' ? '#f6ffed' : '#fff'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ 
                width: '40px', 
                height: '40px', 
                borderRadius: '50%', 
                backgroundColor: '#52c41a',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '16px'
              }}>
                <FileCheck size={20} />
              </div>
              <div>
                <div style={{ fontWeight: 'bold', fontSize: '14px' }}>Accepted</div>
                <div style={{ color: '#666', fontSize: '12px' }}>{statusStats['Accepted']} records</div>
              </div>
            </div>
          </Card>

          {/* Rejected Status Box */}
          <Card
            hoverable
            onClick={() => handleStatusFilterClick('Rejected')}
            style={{
              cursor: 'pointer',
              border: visualSelectedStatus === 'Rejected' ? '2px solid #ff4d4f' : '1px solid #d9d9d9',
              backgroundColor: visualSelectedStatus === 'Rejected' ? '#fff2f0' : '#fff'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ 
                width: '40px', 
                height: '40px', 
                borderRadius: '50%', 
                backgroundColor: '#ff4d4f',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '16px'
              }}>
                <CircleX size={20} />
              </div>
              <div>
                <div style={{ fontWeight: 'bold', fontSize: '14px' }}>Rejected</div>
                <div style={{ color: '#666', fontSize: '12px' }}>{statusStats['Rejected']} records</div>
              </div>
            </div>
          </Card>
        </div>
      </div>

                           {/* Second Container - Table (remaining height) */}
        <div className="table-container">
          {/* Action Buttons Row */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            marginBottom: '16px',
            gap: '16px'
          }}>
            <div style={{ display: 'flex', gap: '8px' }}>
            </div>

            <div style={{ display: 'flex', gap: '8px', position: 'relative' }}>
              {/* Columns Button */}
              <Button
                icon={<SettingOutlined />}
                onClick={() => setShowColumnsDropdown(!showColumnsDropdown)}
                size="middle"
                type="default"
                title="Column settings"
                data-columns-button
              >
                Columns
              </Button>

              {/* Columns Dropdown Menu */}
              {showColumnsDropdown && (
                <div
                  data-columns-dropdown
                  style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    zIndex: 1000,
                    backgroundColor: 'white',
                    border: '1px solid #d9d9d9',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    padding: '12px',
                    minWidth: '220px',
                    maxHeight: '400px',
                    overflowY: 'auto',
                    marginTop: '4px'
                  }}
                >
                  <div style={{ 
                    fontSize: '14px', 
                    fontWeight: 'bold', 
                    marginBottom: '12px',
                    color: '#262626',
                    borderBottom: '1px solid #f0f0f0',
                    paddingBottom: '8px'
                  }}>
                    Column Visibility
                  </div>
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={columnOrder}
                      strategy={verticalListSortingStrategy}
                    >
                      <div style={{ 
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px'
                      }}>
                        {getOrderedColumnConfig().map((column) => (
                          <SortableItem
                            key={column.key}
                            id={column.key}
                            label={column.label}
                            checked={columnVisibility[column.key]}
                            onToggle={handleColumnVisibilityChange}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                </div>
              )}

              {/* Clear Table Filters Button */}
              <Button
                icon={<ClearOutlined />}
                onClick={handleClearTableFilters}
                size="middle"
                type="default"
                title="Clear table column filters and sorting"
              >
                Clear Table Filters
              </Button>
            </div>
          </div>

                     <Card title="Insurance Data" className="table-card">
                                                <Table
                key={tableKey}
                ref={tableRef}
                columns={selectedStatus === 'Rejected' ? (rejectedColumns as any) : (getVisibleColumns(columns) as any)}
                dataSource={filteredData}
                rowKey="id"
                loading={loading}
                pagination={{
                  current: currentPage,
                  pageSize: pageSize,
                  total: totalCount,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  pageSizeOptions: [10, 20, 50, 100],
                  showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                  onChange: (page, size) => {
                    setCurrentPage(page);
                    setPageSize(size || 10);
                  },
                }}
                scroll={{ x: 1400 }}
                size="middle"
              />
          </Card>
       </div>

      {/* Upload Modal */}
      <Modal
        title="Add New Demographics"
        open={isModalVisible}
        onOk={handleModalSubmit}
        onCancel={handleModalCancel}
        width={600}
        confirmLoading={fileState.isUploading}
        okText="Upload"
        cancelText="Cancel"
      >
        <div className="modal-form">
          <div className="form-item">
            <label>Select Doctor:</label>
            <Select
              placeholder="Choose a doctor..."
              value={fileState.selectedDoctor}
              onChange={handleDoctorChange}
              style={{ width: '100%' }}
              loading={loadingDoctors}
            >
              {doctors.map((doctor, index) => (
                <Option key={index} value={doctor.employeeId}>{doctor.name}</Option>
              ))}
            </Select>
          </div>

          <div className="form-item">
            <label>Select Procedure:</label>
            <Select
              placeholder={fileState.selectedDoctor ? "Choose a procedure..." : "Please select a doctor first"}
              value={fileState.selectedProcedure}
              onChange={handleProcedureChange}
              style={{ width: '100%' }}
              disabled={!fileState.selectedDoctor}
              loading={loadingProcedures}
            >
              {availableProcedures.map((procedure, index) => (
                <Option key={index} value={procedure}>{procedure}</Option>
              ))}
            </Select>
          </div>

          <div className="form-item">
            <label>Upload File:</label>
            <Upload
              key={modalKey}
              beforeUpload={() => false}
              onChange={handleFileChange}
              accept=".pdf,.xlsx,.csv,.txt"
              showUploadList={true}
            >
              <Button icon={<UploadOutlined />}>Choose File</Button>
            </Upload>
          </div>

                     <div className="form-item">
             <Switch
               checked={fileState.enableOnDemand}
               onChange={handleToggleChange}
             />
             <span style={{ marginLeft: '8px' }}>Enable on demand</span>
           </div>

                     {/* Error Display */}
           {fileState.uploadError && (
             <div className="form-item" style={{ marginTop: 16 }}>
               <div style={{ 
                 padding: '12px 16px', 
                 backgroundColor: '#fff2f0', 
                 border: '1px solid #ffccc7', 
                 borderRadius: '8px',
                 color: '#cf1322',
                 whiteSpace: 'pre-line'
               }}>
                 <strong>Upload Error:</strong><br />
                 {fileState.uploadError}
               </div>
             </div>
           )}
        </div>
      </Modal>

      {/* Resubmit Modal */}
      <Modal
        title="Resubmit Demographics Record"
        open={isResubmitModalVisible}
        onOk={handleResubmitSubmit}
        onCancel={handleResubmitCancel}
        width={600}
        confirmLoading={resubmitFileState.isUploading}
        okText="Resubmit"
        cancelText="Cancel"
      >
        <div className="modal-form">
          <div style={{ 
            padding: '12px 16px', 
            backgroundColor: '#f6ffed', 
            border: '1px solid #b7eb8f', 
            borderRadius: '8px',
            marginBottom: '16px',
            color: '#389e0d'
          }}>
            <strong>Resubmit Confirmation:</strong><br />
            You are about to resubmit record ID: {resubmitRecord?.id}. This will update the existing record with new data. You can optionally upload a new file or keep the current one. Please review and confirm your changes.
          </div>

          <div className="form-item">
            <label>Select Doctor:</label>
            <Select
              placeholder="Choose a doctor..."
              value={resubmitFileState.selectedDoctor}
              onChange={(value) => setResubmitFileState(prev => ({ ...prev, selectedDoctor: value }))}
              style={{ width: '100%' }}
              loading={loadingDoctors}
            >
              {doctors.map((doctor, index) => (
                <Option key={index} value={doctor.employeeId}>{doctor.name}</Option>
              ))}
            </Select>
          </div>

          <div className="form-item">
            <label>Select Procedure:</label>
            <Select
              placeholder={resubmitFileState.selectedDoctor ? "Choose a procedure..." : "Please select a doctor first"}
              value={resubmitFileState.selectedProcedure}
              onChange={(value) => setResubmitFileState(prev => ({ ...prev, selectedProcedure: value }))}
              style={{ width: '100%' }}
              disabled={!resubmitFileState.selectedDoctor}
              loading={loadingProcedures}
            >
              {availableProcedures.map((procedure, index) => (
                <Option key={index} value={procedure}>{procedure}</Option>
              ))}
            </Select>
          </div>

          <div className="form-item">
            <label>Current File:</label>
            <div style={{ 
              padding: '8px 12px', 
              backgroundColor: '#f5f5f5', 
              border: '1px solid #d9d9d9', 
              borderRadius: '8px',
              marginBottom: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FileTextOutlined style={{ color: '#1890ff' }} />
                <span>{resubmitRecord?.filename}</span>
              </div>
              <Button
                type="link"
                size="small"
                onClick={() => handleViewPDF(resubmitRecord?.id || '')}
                style={{ padding: '0 4px' }}
              >
                View
              </Button>
            </div>
          </div>

          <div className="form-item">
            <label>Upload New File (Optional):</label>
            <Upload
              beforeUpload={() => false}
              onChange={(info) => {
                const file = info.file.originFileObj || info.file;
                if (file) {
                  const allowedTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'text/csv'];
                  if (file.type && allowedTypes.includes(file.type)) {
                    setResubmitFileState(prev => ({ ...prev, selectedFile: file as File, uploadError: null }));
                  } else {
                    message.error('Invalid file type. Only PDF, Excel, and CSV files are allowed.');
                    setResubmitFileState(prev => ({ ...prev, selectedFile: null, uploadError: null }));
                  }
                }
              }}
              accept=".pdf,.xlsx,.csv,.txt"
              showUploadList={true}
            >
              <Button icon={<UploadOutlined />}>Choose New File</Button>
            </Upload>
            <div style={{ 
              fontSize: '12px', 
              color: '#666', 
              marginTop: '4px',
              fontStyle: 'italic'
            }}>
              Leave empty to keep the current file
            </div>
          </div>

          <div className="form-item">
            <Switch
              checked={resubmitFileState.enableOnDemand}
              onChange={(checked) => setResubmitFileState(prev => ({ ...prev, enableOnDemand: checked }))}
            />
            <span style={{ marginLeft: '8px' }}>Enable on demand</span>
          </div>

          {/* Error Display */}
          {resubmitFileState.uploadError && (
            <div className="form-item" style={{ marginTop: 16 }}>
              <div style={{ 
                padding: '12px 16px', 
                backgroundColor: '#fff2f0', 
                border: '1px solid #ffccc7', 
                borderRadius: '8px',
                color: '#cf1322',
                whiteSpace: 'pre-line'
              }}>
                <strong>Upload Error:</strong><br />
                {resubmitFileState.uploadError}
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* Notes Modal */}
      <Modal
        title={`Order Notes for #${selectedRecord?.id || ''}`}
        open={isNotesModalVisible}
        onCancel={handleCloseNotesModal}
        footer={null}
        width={600}
        centered
      >
        {selectedRecord && !isNotesReadOnly(selectedRecord) && (
          <div style={{ marginBottom: '16px' }}>
            <Input.TextArea
              placeholder="Add a new note..."
              value={newNoteText}
              onChange={(e) => setNewNoteText(e.target.value)}
              rows={3}
              disabled={addingNote}
              style={{ marginBottom: '8px' }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <Button
                type="primary"
                onClick={handleAddNote}
                disabled={!newNoteText.trim() || addingNote}
                loading={addingNote}
                size="small"
              >
                Save Note
              </Button>
              <Button
                onClick={() => setNewNoteText('')}
                disabled={addingNote}
                size="small"
              >
                Clear
              </Button>
            </div>
            {addingNote && (
              <div style={{ textAlign: 'center', marginTop: '8px' }}>
                <Spin size="small" /> Adding note...
              </div>
            )}
          </div>
        )}

        {selectedRecord && isNotesReadOnly(selectedRecord) && (
          <div style={{ 
            marginBottom: '16px', 
            padding: '12px', 
            backgroundColor: '#f6ffed', 
            border: '1px solid #b7eb8f', 
            borderRadius: '6px',
            color: '#389e0d'
          }}>
            <strong>Read-Only Mode:</strong> This record is locked. Notes can only be viewed, not modified.
          </div>
        )}

        {/* Role Information Banner */}
        <div style={{ 
          marginBottom: '16px', 
          padding: '10px', 
          backgroundColor: '#e6f7ff', 
          border: '1px solid #91d5ff', 
          borderRadius: '6px',
          fontSize: '12px',
          color: '#0050b3'
        }}>
          <strong>Note:</strong> {user?.role === 'admin' ? 'Admins can delete any note' : 'You can only delete notes you created'}
        </div>

        <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
          {notesLoading ? (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <Spin size="large" />
            </div>
          ) : notes.length === 0 ? (
            <Empty 
              description="No notes yet" 
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ) : (
            <List
              dataSource={notes}
              renderItem={(note) => (
                <List.Item
                  actions={[
                    // Only show delete button if user can delete this note and record is not read-only
                    selectedRecord && !isNotesReadOnly(selectedRecord) && canDeleteNote(note) && (
                      <Button
                        key="delete"
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => {
                          Modal.confirm({
                            title: 'Delete Note',
                            content: 'Are you sure you want to delete this note?',
                            okText: 'Yes',
                            cancelText: 'No',
                            onOk: () => deleteNote(note._id)
                          });
                        }}
                        size="small"
                      />
                    )
                  ].filter(Boolean)}
                >
                  <List.Item.Meta
                    title={
                      <div style={{ fontSize: '14px', fontWeight: 'normal' }}>
                        {note.text}
                      </div>
                    }
                    description={
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        Added by {note.addedBy}
                        {note.addedByRole && (
                          <Tag 
                            color={
                              note.addedByRole === 'admin' ? 'red' :
                              note.addedByRole === 'practitioner' ? 'blue' :
                              note.addedByRole === 'manufacturer' ? 'green' :
                              note.addedByRole === 'insurance' ? 'orange' :
                              'default'
                            }
                            style={{ marginLeft: '8px', fontSize: '11px' }}
                          >
                            {note.addedByRole.charAt(0).toUpperCase() + note.addedByRole.slice(1)}
                          </Tag>
                        )}
                        {' • '}{new Date(note.createdAt).toLocaleString()}
                      </div>
                    }
                  />
                </List.Item>
              )}
            />
          )}
        </div>
      </Modal>

      {/* Insurance Upload Modal */}
      <Modal
        title={`Upload Insurance File - Record #${selectedRecordForInsurance?.id || ''}`}
        open={isInsuranceUploadModalVisible}
        onOk={handleInsuranceFileUpload}
        onCancel={() => {
          setIsInsuranceUploadModalVisible(false);
          setSelectedRecordForInsurance(null);
          setInsuranceFileState({
            selectedFile: null,
            isUploading: false,
            uploadError: null
          });
        }}
        width={500}
        confirmLoading={insuranceFileState.isUploading}
        okText="Upload"
        cancelText="Cancel"
      >
        <div className="modal-form">
          <div style={{ marginBottom: '16px' }}>
            <p><strong>Patient:</strong> {selectedRecordForInsurance ? 
              [selectedRecordForInsurance.firstName, selectedRecordForInsurance.middleName, selectedRecordForInsurance.lastName]
                .filter(name => name && name.trim())
                .join(' ') : ''
            }</p>
            <p><strong>Doctor:</strong> {selectedRecordForInsurance?.doctorName}</p>
            <p><strong>Procedure:</strong> {selectedRecordForInsurance?.procedureName}</p>
          </div>

          <div className="form-item">
            <label>Upload Insurance File (PDF only):</label>
            <Upload
              beforeUpload={() => false}
              onChange={(info) => {
                const file = info.file.originFileObj || info.file;
                if (file) {
                  if (file.type === 'application/pdf') {
                    setInsuranceFileState(prev => ({ 
                      ...prev, 
                      selectedFile: file as File, 
                      uploadError: null 
                    }));
                  } else {
                    message.error('Only PDF files are allowed for insurance verification');
                    setInsuranceFileState(prev => ({ 
                      ...prev, 
                      selectedFile: null, 
                      uploadError: 'Only PDF files are allowed' 
                    }));
                  }
                }
              }}
              accept=".pdf"
              showUploadList={true}
            >
              <Button icon={<UploadOutlined />}>Choose PDF File</Button>
            </Upload>
          </div>

          {/* Error Display */}
          {insuranceFileState.uploadError && (
            <div className="form-item" style={{ marginTop: 16 }}>
              <div style={{ 
                padding: '12px 16px', 
                backgroundColor: '#fff2f0', 
                border: '1px solid #ffccc7', 
                borderRadius: '8px',
                color: '#cf1322'
              }}>
                <strong>Upload Error:</strong><br />
                {insuranceFileState.uploadError}
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* Accept Insurance Modal */}
      <Modal
        title={`Accept Insurance Verification - Record #${selectedRecordForAccept?.id || ''}`}
        open={isAcceptModalVisible}
        onOk={handleAcceptModalSubmit}
        onCancel={() => {
          setIsAcceptModalVisible(false);
          setSelectedRecordForAccept(null);
          setSelectedCarrier('');
        }}
        width={500}
        okText="Accept"
        cancelText="Cancel"
      >
        <div className="modal-form">
          <div style={{ marginBottom: '16px' }}>
            <p><strong>Patient:</strong> {selectedRecordForAccept ? 
              [selectedRecordForAccept.firstName, selectedRecordForAccept.middleName, selectedRecordForAccept.lastName]
                .filter(name => name && name.trim())
                .join(' ') : ''
            }</p>
            <p><strong>Doctor:</strong> {selectedRecordForAccept?.doctorName}</p>
            <p><strong>Procedure:</strong> {selectedRecordForAccept?.procedureName}</p>
          </div>

          <div className="form-item">
            <label>Select Insurance Carrier (Required):</label>
            <Select
              placeholder="Choose an insurance carrier..."
              value={selectedCarrier}
              onChange={setSelectedCarrier}
              style={{ width: '100%' }}
              loading={loadingInsuranceCarriers}
              notFoundContent={loadingInsuranceCarriers ? <span>Loading...</span> : <span>No carriers found</span>}
            >
              {insuranceCarriers.map((carrier) => (
                <Option key={carrier._id} value={carrier._id}>
                  {carrier.name}
                </Option>
              ))}
            </Select>
          </div>

          {selectedCarrier && (
            <div style={{ 
              marginTop: '12px',
              padding: '8px 12px', 
              backgroundColor: '#f6ffed', 
              border: '1px solid #b7eb8f', 
              borderRadius: '6px',
              fontSize: '12px',
              color: '#389e0d'
            }}>
              <strong>Selected Carrier:</strong> {insuranceCarriers.find(c => c._id === selectedCarrier)?.name}
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default InsurancePage;
